import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  LinearProgress,
  Typography
} from "@mui/material";

import { getCorrespondences } from "../../services/backend/CorrespondenceService";
import { fetchFiledPage } from "../../services/backend/FiledSearchService";
import FiledItem from "./FiledItem";
import FiledItemFilter from "./FiledItemFilter";
import useFiledSearch, { filedLoadErrorMessage } from "./useFiledSearch";
import { setCorrespondence } from "../../redux/slices/correspondenceSlice";
import { PERMISSIONS } from "../../constants";

export default function FiledList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const correspondences = useSelector((state) => state.correspondences.value);
  const [correspondenceSelected, setCorrespondenceSelected] = useState("all");

  // Only the subseries that actually produce a radicado are selectable here.
  const filedTypes = useMemo(
    () => correspondences?.filter((corresp) => corresp.resultType === 'filed') || [],
    [correspondences]
  );

  const {
    items,
    search,
    setSearch,
    isSearching,
    range,
    applyRange,
    loading,
    loadingMore,
    cascading,
    hasMore,
    loadError,
    statusMessage,
    sentinelRef,
    refresh,
  } = useFiledSearch({ fetchPage: fetchFiledPage, filedType: correspondenceSelected });

  const updateCorrespondences = async () => {
    if (correspondences) return;
    const responseJSON = await getCorrespondences(user.token);
    dispatch(setCorrespondence(responseJSON));
  };

  useEffect(() => {
    if (!user.permissions.includes(PERMISSIONS.FILED.LIST)) return navigate(`/home`);
    updateCorrespondences();
  }, []); // eslint-disable-line

  return (
    <Container maxWidth={false}>
      <FiledItemFilter
        handleFilter={applyRange}
        hasButton
        search={search}
        onSearchChange={setSearch}
        activeRange={range.key}
        correspondences={filedTypes}
        filedType={correspondenceSelected}
        onFiledTypeChange={setCorrespondenceSelected}
        onRefresh={refresh}
        busy={loading || loadingMore || cascading}
        statusMessage={statusMessage}
      />
      {loadError && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
          {filedLoadErrorMessage(loadError)}
        </Alert>
      )}
      {(cascading || loadingMore) && <LinearProgress color="primary" sx={{ height: 4 }} />}
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
        {loading ? (
          <CircularProgress sx={{ margin: 'auto', my: 4 }} />
        ) : items.length > 0 ? (
          items.map((item) => (
            <FiledItem key={item.filedId} item={item} match={item.match} />
          ))
        ) : (
          <Typography sx={{ m: 4 }} color="text.secondary">
            {isSearching
              ? `No hay radicados que coincidan con «${search}» en este periodo.`
              : "No hay ningún radicado con el filtro seleccionado"}
          </Typography>
        )}
      </Box>
      {hasMore && !loading && <Box ref={sentinelRef} sx={{ width: "100%", py: 2 }} />}
    </Container>
  );
}
